import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import indexApi from '@/apis'
import { ResultSkinRecommendedProduct } from '@/pages/application-flow-skins/types'

interface LegacyProduct {
  id: string
  lendingRateString?: number
  loansLimitMax?: number
  productLogo?: string
  productName?: string
}

interface ApplyResult {
  h5Protocols?: unknown[]
  thirdpartyTargetUrl?: string
  unionLoginFlag?: number
}

interface RecommendedProductsResponse {
  hotRecommends?: LegacyProduct[]
  jumboLoanRecommends?: LegacyProduct[]
  microfinanceRecommends?: LegacyProduct[]
}

export interface ResultJointLoginPayload {
  appH5: number
  entranceType: number
  h5Protocols: unknown[]
  loansLimitMax?: number
  productId: string
  productLogo?: string
  productName?: string
}

interface Props {

  /** 仅在旧容器已确定渲染 v2 产品结果分支时请求产品，避免影响 v1 页面。 */
  enabled: boolean

  /** 打开旧联登弹窗的回调，弹窗仍由旧结果容器挂载。 */
  onOpenJointLogin: (payload: ResultJointLoginPayload) => void
}

const FEATURED_ENTRANCE_TYPE = 4
const RECOMMENDED_ENTRANCE_TYPE = 6
const LENDING_RATE_BASE = 88
const LENDING_RATE_VARIATION = 6

const toSkinProduct = (product: LegacyProduct): ResultSkinRecommendedProduct => ({
  id: product.id,
  lendingRate: product.lendingRateString,
  limit: product.loansLimitMax,
  logo: product.productLogo,
  name: product.productName || '',
})

/**
 * useV2ResultBusinessBridge — 保留旧结果页的产品请求、申请、联登与曝光副作用。
 * @returns {object} 供结果皮肤消费的产品展示数据与旧业务动作
 */
const useV2ResultBusinessBridge = ({ enabled, onOpenJointLogin }: Props) => {
  const [featuredProducts, setFeaturedProducts] = useState<LegacyProduct[]>([])
  const [recommendedProducts, setRecommendedProducts] = useState<LegacyProduct[]>([])
  const exposedProductIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    let active = true

    if (!enabled) {
      return () => {
        active = false
      }
    }

    indexApi.topRecommend().then(response => {
      if (!active) {
        return
      }
      const products = Array.isArray(response) ? response as LegacyProduct[] : []
      const productsWithRate = products.map(product => ({
        ...product,
        // 原页面的放款率本就是纯展示随机值，不参与申请参数或业务分流。
        lendingRateString: Math.round(Math.random() * LENDING_RATE_VARIATION + LENDING_RATE_BASE),
      }))
      setFeaturedProducts(productsWithRate)
    })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [enabled])

  useEffect(() => {
    let active = true

    if (!enabled) {
      return () => {
        active = false
      }
    }

    indexApi.homeProductList({ appH5: 2 }).then(response => {
      if (!active) {
        return
      }
      const products = response as RecommendedProductsResponse
      setRecommendedProducts([
        ...(products.hotRecommends || []),
        ...(products.jumboLoanRecommends || []),
        ...(products.microfinanceRecommends || []),
      ])
    })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [enabled])

  const reportProductExposure = useCallback((productId: string, entranceType: number) => {
    if (exposedProductIds.current.has(productId)) {
      return
    }
    exposedProductIds.current.add(productId)
    indexApi.reportProductExposure({
      appH5: 2,
      entranceType,
      productId,
    })
  }, [])

  const applyProduct = useCallback(async(product: LegacyProduct, entranceType: number) => {
    const response = await indexApi.applyFor({
      appH5: 2,
      entranceType,
      productId: product.id,
    }) as ApplyResult

    if (response.unionLoginFlag === 1) {
      onOpenJointLogin({
        appH5: 2,
        entranceType,
        h5Protocols: response.h5Protocols || [],
        loansLimitMax: product.loansLimitMax,
        productId: product.id,
        productLogo: product.productLogo,
        productName: product.productName,
      })
      return
    }
    if (response.thirdpartyTargetUrl) {
      window.location.href = response.thirdpartyTargetUrl
    }
  }, [onOpenJointLogin])

  const featuredProductById = useMemo(() => new Map(featuredProducts.map(product => [product.id, product])), [featuredProducts])
  const recommendedProductById = useMemo(() => new Map(recommendedProducts.map(product => [product.id, product])), [recommendedProducts])

  const handleApplyFeaturedProduct = useCallback((productId: string) => {
    const product = featuredProductById.get(productId)
    if (!product) {
      return
    }
    applyProduct(product, FEATURED_ENTRANCE_TYPE)
  }, [applyProduct, featuredProductById])

  const handleExposeFeaturedProduct = useCallback((productId: string) => {
    if (featuredProductById.has(productId)) {
      reportProductExposure(productId, FEATURED_ENTRANCE_TYPE)
    }
  }, [featuredProductById, reportProductExposure])

  const handleApplyRecommendedProduct = useCallback((productId: string) => {
    const product = recommendedProductById.get(productId)
    if (product) {
      applyProduct(product, RECOMMENDED_ENTRANCE_TYPE)
    }
  }, [applyProduct, recommendedProductById])

  return {
    featuredProducts: featuredProducts.map(toSkinProduct),
    handleApplyFeaturedProduct,
    handleApplyRecommendedProduct,
    handleExposeFeaturedProduct,
    recommendedProducts: recommendedProducts.map(toSkinProduct),
  }
}

export default useV2ResultBusinessBridge
